import React, { useEffect, useState } from "react";
import { Pause, Play } from "lucide-react";
import { useAudioPlayer } from "./GlobalAudioPlayer";
import { modelVoices } from "@/utils/tools";
import { useT } from "@jammable/ui-core/src/i18n";

// Fixed bar heights so server and client render the same waveform.
const WAVE = [8, 14, 20, 12, 24, 16, 10, 22, 18, 9, 15, 26, 12, 19, 8, 14];

// modelVoices names are "Male Singing 1" etc.; shown translated.
const demoLabel = (t, name) => {
  const m = /^(Male|Female) Singing (\d+)$/.exec(name || "");
  if (!m) return name;
  return t(
    m[1] === "Female" ? "voice.demos.femaleSinging" : "voice.demos.maleSinging",
    { n: m[2] },
  );
};

const TryVoiceSection = ({ modelData }) => {
  const t = useT();
  const voices = modelVoices;
  const [sortedVoices, setSortedVoices] = useState([]);
  const { playAudio, info, pauseAudio, isPlaying } = useAudioPlayer();
  const getGenderFromVoice = (voice) => {
    return voice.name.toLowerCase().includes("female") ? "female" : "male";
  };

  useEffect(() => {
    if (modelData && voices) {
      const modelGender = modelData?.tts?.gender;

      // Attach an index to each voice for secondary sorting
      const voicesWithIndex = voices.map((voice, index) => ({
        ...voice,
        index,
      }));

      // Sort voices based on the model's gender, with secondary sorting by original index
      const sorted = [...voicesWithIndex].sort((a, b) => {
        let aGender = getGenderFromVoice(a);
        let bGender = getGenderFromVoice(b);

        if (aGender === bGender) {
          // If genders are the same, sort by original index
          return a.index - b.index;
        } else if (modelGender === "female") {
          // Prioritize female voices if model is female
          return aGender === "female" ? -1 : 1;
        } else {
          // Prioritize male voices if model is male
          return aGender === "male" ? -1 : 1;
        }
      });

      setSortedVoices(sorted);
    }
  }, [modelData, voices]);

  const hasSuccessfulDemo = modelData?.demos?.some(
    (d) => d?.status !== "failed",
  );

  if (!hasSuccessfulDemo) {
    return null;
  }

  return (
    <section className="tw-flex tw-w-full tw-flex-col tw-gap-3.5">
      <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1">
        <h2 className="jam-display tw-m-0 tw-text-[22px] tw-font-extrabold tw-text-jam-ink md:tw-text-[28px]">
          {t("voice.demos.title")}
        </h2>
        <span className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
          {t("voice.demos.note")}
        </span>
      </div>
      <div className="tw-grid tw-grid-cols-2 tw-gap-2 md:tw-gap-2.5">
        {sortedVoices?.map((demo) => {
          const isDemo = modelData?.demos?.find((d) => d?.name === demo?.param);
          const playing =
            isPlaying && info?.id === `${modelData?._id}-${demo?.name}`;
          const label = demoLabel(t, demo?.name);

          return isDemo?.status === "failed" ? null : (
            <div
              key={demo?.name}
              className="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-[20px] tw-bg-jam-lav tw-p-2.5 md:tw-gap-3 md:tw-rounded-[24px] md:tw-p-3"
            >
              <button
                type="button"
                aria-label={
                  playing
                    ? t("voice.pause", { name: label })
                    : t("voice.play", { name: label })
                }
                onClick={() => {
                  if (playing) {
                    pauseAudio();
                  } else {
                    playAudio({
                      audio: isDemo?.convertedUrl,
                      info: {
                        image: modelData?.image?.url,
                        name: label,
                        id: `${modelData?._id}-${demo?.name}`,
                        artistNames: modelData?.artistname,
                      },
                    });
                  }
                }}
                className="tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-white md:tw-h-11 md:tw-w-11"
              >
                {playing ? (
                  <Pause size={14} fill="currentColor" strokeWidth={0} />
                ) : (
                  <Play size={14} fill="currentColor" strokeWidth={0} />
                )}
              </button>
              <div className="tw-min-w-0 tw-flex-grow">
                <div className="tw-truncate tw-text-xs tw-font-extrabold tw-text-jam-ink md:tw-text-sm">
                  {label}
                </div>
                <div className="tw-hidden tw-text-xs tw-font-semibold tw-text-jam-muted md:tw-block">
                  {t("voice.demos.english")}
                </div>
              </div>
              {/* Decorative waveform, desktop only. */}
              <div
                aria-hidden
                className="tw-hidden tw-h-7 tw-items-center tw-gap-0.5 xl:tw-flex"
              >
                {WAVE.map((h, i) => (
                  <span
                    key={i}
                    style={{ height: h }}
                    className={`tw-w-[3px] tw-rounded-sm ${playing ? "tw-bg-jam-purple" : "tw-bg-jam-soft"}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default TryVoiceSection;
